import type { IconDefinition } from '../../shared/iconDefinition';
import type { ReactionBucketRow } from '../../shared/reactionBucketRow';
import { formatVideoClock } from '../formatVideoClock';
import { TwemojiGlyphImage } from '../twemojiGlyphImage';

const MAX_ROWS = 10;

// Buckets ranked by reaction count, each row seeking the player to that moment.
//
// This table is the closest thing v0 has to answering its own primary success
// criterion (DESIGN-DECISIONS §9): do reactions cluster? If the top few rows hold
// a wildly disproportionate share of the taps, moments are real. If every row
// shows roughly the same count, there are no moments and the timestamp layer is
// decoration. Either way the creator sees it before we do.
export function TopMomentsTable(input: {
  buckets: ReactionBucketRow[];
  icons: IconDefinition[];
  bucketMs: number;
  onSeek: (ms: number) => void;
}) {
  const ranked = [...input.buckets].sort((a, b) => b.total - a.total || a.t_ms - b.t_ms).slice(0, MAX_ROWS);

  if (ranked.length === 0) {
    return <p className="mutedNote">No reactions yet, so there are no moments to rank.</p>;
  }

  return (
    <table className="momentsTable">
      <thead>
        <tr>
          <th>When</th>
          <th>What landed</th>
          <th>How many</th>
        </tr>
      </thead>
      <tbody>
        {ranked.map((bucket) => {
          // Show the icons that actually fired in this bucket, busiest first, so
          // the row says what kind of moment it was and not just how loud.
          const topIcons = Object.entries(bucket.by_icon)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5);

          return (
            <tr key={bucket.t_ms}>
              <td>
                <button className="seekButton" type="button" onClick={() => input.onSeek(bucket.t_ms)}>
                  {formatVideoClock(bucket.t_ms)}
                </button>
                <span className="bucketSpan">
                  –{formatVideoClock(bucket.t_ms + input.bucketMs)}
                </span>
              </td>
              <td>
                <span className="momentIcons">
                  {topIcons.map(([iconId, count]) => {
                    const icon = input.icons.find((candidate) => candidate.id === iconId);
                    if (!icon) return null;
                    return (
                      <span className="momentIcon" key={iconId} title={`${icon.label}: ${count}`}>
                        <TwemojiGlyphImage codepoint={icon.glyph_cp} label={icon.label} size={20} />
                        <i>{count}</i>
                      </span>
                    );
                  })}
                </span>
              </td>
              {/* Reach beside taps (Cowork004 §2). Reviewers are told to tap
                  repeatedly when something hits hard, so a big `total` can mean
                  nine people reacting or one person mashing. Those are different
                  moments and the creator needs to be able to tell them apart.
                  Reach leads, because consensus is the harder number to infer. */}
              <td className="momentTotal">
                <b>
                  {bucket.reach} {bucket.reach === 1 ? 'person' : 'people'}
                </b>
                <span className="momentTaps">
                  {bucket.total} {bucket.total === 1 ? 'tap' : 'taps'}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
